/**
 * Wizard progress for the create-CV flow (web twin of `CreateCvProgress.tsx`).
 *
 * Replaces the old tab row: the two halves of this flow are sequential, so the
 * chrome has to say "step 2 of 2", not offer a sideways jump into a step whose
 * input does not exist yet.
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  /** 1-based. */
  stepIndex: number;
  totalSteps: number;
  label: string;
}

export function CreateCvProgress({ stepIndex, totalSteps, label }: Props) {
  const { t } = useTranslation('seeker');

  return (
    <div
      role="progressbar"
      aria-valuenow={stepIndex}
      aria-valuemin={1}
      aria-valuemax={totalSteps}
      style={WRAP}
    >
      <div style={LABEL_ROW}>
        <span style={LABEL}>{label}</span>
        {totalSteps > 1 ? (
          <span style={COUNTER}>
            {t('cv.create.stepOf', { current: stepIndex, total: totalSteps })}
          </span>
        ) : null}
      </div>
      {/* A one-step flow has no progress to show: a single full-width bar reads
          as a heading underline, not as an indicator. */}
      {totalSteps > 1 ? (
        <div style={TRACK}>
          {Array.from({ length: totalSteps }, (_, index) => (
            <span
              key={index}
              style={{
                ...SEGMENT,
                backgroundColor:
                  index < stepIndex
                    ? 'var(--text-primary)'
                    : 'var(--border-subtle, rgba(0,0,0,0.12))',
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

const WRAP: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const LABEL_ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
};

const LABEL: CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  lineHeight: '20px',
  color: 'var(--text-primary)',
};

const COUNTER: CSSProperties = {
  fontSize: 13,
  color: 'var(--text-tertiary)',
  flexShrink: 0,
};

const TRACK: CSSProperties = {
  display: 'flex',
  gap: 5,
};

const SEGMENT: CSSProperties = {
  flex: 1,
  height: 4,
  borderRadius: 9999,
  transition: 'background-color 200ms',
};
